import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Modal,
  PasswordInput,
  Checkbox,
  Stack,
  Loading,
} from '@carbon/react';

import { showToast } from 'services/toasts';
import { useChangePasswordMutation } from 'services/user';

const MIN_LENGTH = 8;

/**
 * Admin modal to set a new password for another user.
 *
 * @param {object} props
 * @param {{id: (string|number), email?: string}} props.user - target user
 * @param {() => void} props.hideModal - closes the modal
 */
const ChangePasswordModal = ({ user, hideModal }) => {
  const dispatch = useDispatch();
  const [changePassword, { isLoading }] = useChangePasswordMutation();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [sendMail, setSendMail] = useState(false);
  const [error, setError] = useState(null);

  const userID = user?.id;

  // Validate locally before hitting the endpoint. Returns an error string
  // or null when the input is acceptable.
  const validate = () => {
    if (password.length < MIN_LENGTH) {
      return `Das Passwort muss mindestens ${MIN_LENGTH} Zeichen lang sein`;
    }
    if (password !== confirm) {
      return 'Die Passwörter stimmen nicht überein';
    }
    return null;
  };

  const handleSubmit = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    setError(null);

    try {
      await changePassword({
        id: userID,
        new_password: password,
        send_mail: sendMail,
      }).unwrap();

      dispatch(
        showToast({
          kind: 'success',
          title: 'Kennwort geändert',
          caption: `Das Kennwort wurde erfolgreich geändert${
            sendMail ? ' und per E-Mail versendet' : ''
          }`,
        })
      );
      hideModal();
    } catch (err) {
      const backendMessage =
        err?.data?.message ||
        (typeof err?.data === 'string' && err.data) ||
        err?.message;
      dispatch(
        showToast({
          kind: 'error',
          title: 'Fehler',
          caption: backendMessage || 'Das Kennwort konnte nicht geändert werden',
        })
      );
    }
  };

  return (
    <Modal
      open={true}
      size="sm"
      primaryButtonText="Kennwort ändern"
      secondaryButtonText="Abbrechen"
      primaryButtonDisabled={isLoading}
      modalHeading="Kennwort ändern"
      modalLabel={user?.email || ''}
      onRequestClose={hideModal}
      onRequestSubmit={handleSubmit}
    >
      <Loading active={isLoading} withOverlay={true} />
      <Stack gap={5}>
        <PasswordInput
          id="change-password-new"
          labelText="Neues Kennwort"
          value={password}
          invalid={!!error}
          invalidText={error || ''}
          onChange={(e) => setPassword(e.target.value)}
        />
        <PasswordInput
          id="change-password-confirm"
          labelText="Kennwort bestätigen"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
        />
        <Checkbox
          id="change-password-send-mail"
          labelText="Neues Kennwort per E-Mail an den Benutzer senden"
          checked={sendMail}
          onChange={(_e, { checked }) => setSendMail(checked)}
        />
      </Stack>
    </Modal>
  );
};

export default ChangePasswordModal;
